/** Canonical defaults — Arabic-first, aligned with inventory submodules. */
export const DEFAULT_INVENTORY_CONFIG = {
  // المستودعات
  enableWarehouses: true,
  warehouseReferencePrefix: 'WH',
  autoGenerateWarehouseReference: true,
  enableMultipleWarehouses: true,
  requireLocationTracking: false,
  enableBarcodeScanning: true,

  // حركات المخزون
  enableMovements: true,
  movementReferencePrefix: 'MVT',
  autoGenerateMovementReference: true,
  auditAllMovements: true,
  trackSerialNumbers: false,

  // الحجز
  enableReservations: true,
  reservationReferencePrefix: 'RSV',
  autoGenerateReservationReference: true,

  // التحويلات
  enableTransfers: true,
  transferReferencePrefix: 'TRF',
  autoGenerateTransferReference: true,
  requireApprovalForTransfers: true,

  // التلف والفاقد
  enableAdjustments: true,
  adjustmentReferencePrefix: 'ADJ',
  autoGenerateAdjustmentReference: true,
  restrictStockAdjustments: true,
  requireReasonForAdjustment: true,

  // الجرد الفعلي
  enablePhysicalInventory: true,
  physicalInventoryReferencePrefix: 'PINV',
  autoGeneratePhysicalInventoryReference: true,

  // انتهاء الصلاحية
  enableExpiryTracking: true,
  lotReferencePrefix: 'LOT',
  autoGenerateLotReference: true,
  trackBatchNumbers: true,
  trackExpiryDates: true,
  expiryAlertDays: 30,
  alertExpiringSoon: true,

  // تقييم المخزون
  enableValuation: true,
  valuationReferencePrefix: 'VAL',
  autoGenerateValuationReference: true,
  valuationMethod: 'fifo',
  includeShippingInCost: true,
  roundPrices: true,

  // إعادة الطلب
  enableReorder: true,
  reorderReferencePrefix: 'REP',
  autoGenerateReorderReference: true,
  autoReorderEnabled: false,
  reorderPoint: 15,
  defaultLeadTime: 7,
  lowStockThreshold: 10,
  criticalStockThreshold: 5,
  alertLowStock: true,
  alertOverstock: true,
  overstockThreshold: 1000,

  // العرض
  itemsPerPage: 50,
  defaultView: 'table',
  showStockValue: true,
};

const VALUATION_ALIASES = {
  FIFO: 'fifo',
  fifo: 'fifo',
  LIFO: 'lifo',
  lifo: 'lifo',
  PMP: 'average',
  pmp: 'average',
  AVERAGE: 'average',
  average: 'average',
  CUMP: 'average',
  cump: 'average',
};

const LEGACY_VIEW_ALIASES = {
  stock: 'table',
  cards: 'cards',
  table: 'table',
};

function normalizeValuationMethod(value) {
  const key = String(value || '').trim();
  return VALUATION_ALIASES[key] || VALUATION_ALIASES[key.toUpperCase()] || DEFAULT_INVENTORY_CONFIG.valuationMethod;
}

function normalizeDefaultView(value) {
  const v = String(value || '').trim();
  return LEGACY_VIEW_ALIASES[v] || DEFAULT_INVENTORY_CONFIG.defaultView;
}

/** Flatten legacy nested API payload into flat module config. */
function flattenLegacyNestedConfig(raw = {}) {
  if (!raw || typeof raw !== 'object') return {};

  const hasFlatKeys =
    raw.warehouseReferencePrefix != null
    || raw.enableWarehouses != null
    || raw.movementReferencePrefix != null;

  if (hasFlatKeys) return { ...raw };

  const sm = raw.stockManagement || {};
  const val = raw.valuation || {};
  const wh = raw.warehouse || {};
  const mov = raw.movements || {};
  const alerts = raw.alerts || {};
  const sec = raw.security || {};
  const display = raw.display || {};

  return {
    autoReorderEnabled: sm.autoReorderEnabled,
    reorderPoint: sm.reorderPoint,
    defaultLeadTime: sm.defaultLeadTime,
    lowStockThreshold: sm.lowStockThreshold,
    criticalStockThreshold: sm.criticalStockThreshold,
    valuationMethod: val.valuationMethod,
    includeShippingInCost: val.includeShippingInCost,
    roundPrices: val.roundPrices,
    enableMultipleWarehouses: wh.enableMultipleWarehouses,
    requireLocationTracking: wh.requireLocationTracking,
    enableBarcodeScanning: wh.enableBarcodeScanning,
    requireApprovalForTransfers: mov.requireApprovalForTransfers,
    trackSerialNumbers: mov.trackSerialNumbers,
    trackBatchNumbers: mov.trackBatchNumbers,
    trackExpiryDates: mov.trackExpiryDates,
    alertLowStock: alerts.alertLowStock,
    alertExpiringSoon: alerts.alertExpiringSoon,
    expiryAlertDays: alerts.expiryAlertDays,
    alertOverstock: alerts.alertOverstock,
    overstockThreshold: alerts.overstockThreshold,
    restrictStockAdjustments: sec.restrictStockAdjustments,
    requireReasonForAdjustment: sec.requireReasonForAdjustment,
    auditAllMovements: sec.auditAllMovements,
    defaultView: display.defaultView,
    showStockValue: display.showStockValue,
    itemsPerPage: display.itemsPerPage,
  };
}

/** Map legacy API keys and scrub French leftovers. */
export function normalizeInventoryConfig(raw = {}) {
  const flattened = flattenLegacyNestedConfig(raw);
  const merged = { ...DEFAULT_INVENTORY_CONFIG, ...flattened };

  merged.valuationMethod = normalizeValuationMethod(merged.valuationMethod);
  merged.defaultView = normalizeDefaultView(merged.defaultView);

  if (merged.physicalInventoryReferencePrefix == null && raw.physicalInventoryPrefix) {
    merged.physicalInventoryReferencePrefix = raw.physicalInventoryPrefix;
  }

  delete merged.notifyStockMovements;
  delete merged.notifyLowStock;
  delete merged.notifyExpiry;
  delete merged.emailNotifications;
  delete merged.enableReturns;
  delete merged.returnTimeLimit;
  delete merged.requireReturnApproval;
  delete merged.stockManagement;
  delete merged.valuation;
  delete merged.warehouse;
  delete merged.movements;
  delete merged.alerts;
  delete merged.notifications;
  delete merged.security;
  delete merged.display;
  delete merged.returns;

  return merged;
}

export const INVENTORY_ENTITY_PREFIX_MAP = {
  warehouse: 'warehouseReferencePrefix',
  stockMovement: 'movementReferencePrefix',
  stockReservation: 'reservationReferencePrefix',
  transfer: 'transferReferencePrefix',
  adjustment: 'adjustmentReferencePrefix',
  physicalInventory: 'physicalInventoryReferencePrefix',
  lot: 'lotReferencePrefix',
  valuation: 'valuationReferencePrefix',
  replenishment: 'reorderReferencePrefix',
};

export const INVENTORY_AUTO_REFERENCE_MAP = {
  warehouse: 'autoGenerateWarehouseReference',
  stockMovement: 'autoGenerateMovementReference',
  stockReservation: 'autoGenerateReservationReference',
  transfer: 'autoGenerateTransferReference',
  adjustment: 'autoGenerateAdjustmentReference',
  physicalInventory: 'autoGeneratePhysicalInventoryReference',
  lot: 'autoGenerateLotReference',
  valuation: 'autoGenerateValuationReference',
  replenishment: 'autoGenerateReorderReference',
};

/**
 * Map inventory config prefixes into settings.configuration.entityPrefixes keys.
 * Omits `transfer` — caisse/treasury owns entityPrefixes.transfer; inventaire uses getInventoryEntityPrefix.
 */
export function buildInventoryEntityPrefixes(config) {
  const c = normalizeInventoryConfig(config);
  return {
    warehouse: c.warehouseReferencePrefix,
    stockMovement: c.movementReferencePrefix,
    stockReservation: c.reservationReferencePrefix,
    adjustment: c.adjustmentReferencePrefix,
    physicalInventory: c.physicalInventoryReferencePrefix,
    lot: c.lotReferencePrefix,
    valuation: c.valuationReferencePrefix,
    replenishment: c.reorderReferencePrefix,
  };
}

export function getInventoryEntityPrefix(settings, entityKey, fallback) {
  const configKey = INVENTORY_ENTITY_PREFIX_MAP[entityKey];
  const fromInventory = configKey ? settings?.inventory?.[configKey] : null;
  if (fromInventory) return String(fromInventory).trim() || fallback;
  return settings?.configuration?.entityPrefixes?.[entityKey] || fallback;
}

export function isInventoryAutoReferenceEnabled(settings, entityKey) {
  const key = INVENTORY_AUTO_REFERENCE_MAP[entityKey];
  if (!key) return true;
  const val = settings?.inventory?.[key];
  return val !== false;
}

/** Map UI valuation method to backend/calculation codes. */
export function inventoryValuationToCalcMethod(method) {
  const normalized = normalizeValuationMethod(method);
  if (normalized === 'average') return 'PMP';
  if (normalized === 'lifo') return 'LIFO';
  return 'FIFO';
}
